import { css } from '@emotion/react';
import { Box, Button, Icon, Text } from '@inflearn/ds-react-next';
import { VerifyPhoneModal } from '@inflearn/modals';
import { faArrowRight } from '@inflearn/pro-regular-svg-icons';
import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import type { CSSProperties } from 'react';
import { useState } from 'react';

import { AI_INTERVIEWS_URL, MY_URL } from '../../../constants/urls';
import useCheckLoginSession from '../../../hooks/useCheckLoginSession';
import { useMockInterviewCreateFreeMutation } from '../../../mutations/useMockInterviewCreateFreeMutation';
import { useMockInterviewNoLimitCreateMutation } from '../../../mutations/useMockInterviewNoLimitCreateMutation';
import { useLoginModalContext } from '../../../providers/ControlLoginModalContextProvider';
import useClientAccountCredentialsQuery from '../../../queries/useClientAccountCredentialsQuery';
import { useMockInterviewProductQuery } from '../../../queries/useMockInterviewProductQuery';
import {
  getMockInterviewVoucherUserTypeQueryKey,
  useMockInterviewVoucherUserTypeQuery,
} from '../../../queries/useMockInterviewVoucherUserTypeQuery';
import { eventNames } from '../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../utils/GTM/useTrackMixpanelEvent';
import { MockInterviewConsentModal } from './MockInterviewConsentModal';
import { MockInterviewEnvironmentGuideModal } from './MockInterviewEnvironmentGuideModal';
import { LANDING_EASE, LANDING_FONT, LANDING_MOBILE_MQ } from './landing/constants';
import {
  type EnvironmentGuideVariant,
  getEnvironmentGuideVariant,
} from './utils/getEnvironmentGuideVariant';
import { openMockInterviewStartErrorAlert } from './utils/openMockInterviewStartErrorAlert';

export function MockInterviewLandingCtaButton() {
  const { isLoggedIn, isLoading: isCheckLoginSessionLoading } = useCheckLoginSession();
  const { data: userTypeData, isLoading: isGetUserTypeLoading } =
    useMockInterviewVoucherUserTypeQuery({
      options: { enabled: isLoggedIn },
    });
  const credentialsQuery = useClientAccountCredentialsQuery({ enabled: isLoggedIn });
  const productQuery = useMockInterviewProductQuery({
    params: { productCode: 'ONE_TIME' },
    options: { enabled: isLoggedIn },
  });
  const isPhoneVerified = Boolean(credentialsQuery.data?.phoneNumber);
  const isGetClientAccountCredentialsLoading = isLoggedIn && credentialsQuery.isInitialLoading;
  const { trackMixpanelEvent } = useTrackMixpanelEvent();

  const userType = userTypeData?.type;

  if (
    isCheckLoginSessionLoading ||
    (isLoggedIn && isGetUserTypeLoading) ||
    isGetClientAccountCredentialsLoading
  ) {
    return (
      <MockInterviewButton
        label="모의면접 시작하기"
        trackMixpanelEvent={trackMixpanelEvent}
        loading
        disabled
      />
    );
  }

  if (!isLoggedIn) {
    return <AuthButton trackMixpanelEvent={trackMixpanelEvent} />;
  }

  if (!isPhoneVerified) {
    return <PhoneButton trackMixpanelEvent={trackMixpanelEvent} />;
  }

  if (userType === 'NO_LIMIT') {
    return <StartExhibitionMockInterviewButton trackMixpanelEvent={trackMixpanelEvent} />;
  }

  if (userType === 'HAS_VOUCHER') {
    return <RepurchaseMockInterviewButton trackMixpanelEvent={trackMixpanelEvent} />;
  }

  if (userType === 'FREE_AVAILABLE') {
    return <StartFreeMockInterviewButton trackMixpanelEvent={trackMixpanelEvent} />;
  }

  return (
    <StartPaidMockInterviewButton
      trackMixpanelEvent={trackMixpanelEvent}
      product={productQuery.data?.items[0]}
      isProductLoading={productQuery.isLoading}
    />
  );
}

type MockInterviewButtonLabel =
  | '모의면접 시작하기'
  | '무료 면접 시작하기'
  | '다시 도전하기'
  | '모의면접 응시하기';

type TrackMixpanelEvent = ReturnType<typeof useTrackMixpanelEvent>['trackMixpanelEvent'];

type MockInterviewButtonProps = {
  label: MockInterviewButtonLabel;
  trackMixpanelEvent: TrackMixpanelEvent;
  loading?: boolean;
  disabled?: boolean;
  skipEnvironmentGuide?: boolean;
  onClick?: () => void;
};

/**
 * 모든 분기 버튼의 시각 표현을 담당하는 프레젠테이셔널 컴포넌트(다크 글래스 CTA).
 * 클릭 시 모바일이거나 PC라도 화면이 좁으면 분기별 onClick 대신 환경 안내 모달을 띄운다.
 */
function MockInterviewButton({
  label,
  trackMixpanelEvent,
  skipEnvironmentGuide = false,
  onClick,
  ...props
}: MockInterviewButtonProps) {
  const [guideVariant, setGuideVariant] = useState<EnvironmentGuideVariant | null>(null);

  const handleClick = () => {
    trackMixpanelEvent(eventNames.CLICK_BUTTON_AT_MOCK_INTERVIEW, { label });

    const variant = skipEnvironmentGuide ? null : getEnvironmentGuideVariant();

    if (variant) {
      setGuideVariant(variant);
      return;
    }

    onClick?.();
  };

  return (
    <>
      <Button
        type="button"
        css={styles.cta}
        rightSection={
          <Icon icon={faArrowRight} size={16} color="#fff" style={{ width: 16, height: 16 }} />
        }
        onClick={handleClick}
        {...props}
      >
        <Text component="span" css={styles.ctaLabel}>
          {label}
        </Text>
      </Button>
      {guideVariant && (
        <MockInterviewEnvironmentGuideModal
          opened
          variant={guideVariant}
          onClose={() => setGuideVariant(null)}
        />
      )}
    </>
  );
}

type WithTrackMixpanelEvent = {
  trackMixpanelEvent: TrackMixpanelEvent;
};

/** 로그인 분기: 미로그인 상태에서 클릭 시 로그인 모달을 연다. 카피는 신규 분기와 동일. */
function AuthButton({ trackMixpanelEvent }: WithTrackMixpanelEvent) {
  const { open } = useLoginModalContext();

  return (
    <Box css={styles.row}>
      <Text style={textStyles.copyText}>부담 없이 무료 면접 도전해보세요</Text>
      <MockInterviewButton
        label="무료 면접 시작하기"
        trackMixpanelEvent={trackMixpanelEvent}
        onClick={open}
      />
    </Box>
  );
}

/**
 * 전화인증 분기: 미인증 상태에서 클릭 시 인증 모달을 연다. 카피는 신규 분기와 동일.
 * 인증 성공 시 크레덴셜 쿼리를 무효화하면 리렌더되어 상위 컨테이너가 다음 분기로 넘긴다.
 */
function PhoneButton({ trackMixpanelEvent }: WithTrackMixpanelEvent) {
  const queryClient = useQueryClient();
  const [isPhoneModalOpen, phoneModalHandlers] = useDisclosure(false);

  const handleVerifySuccess = () => {
    queryClient.invalidateQueries(useClientAccountCredentialsQuery.queryKey);
    phoneModalHandlers.close();
  };

  return (
    <>
      <Box css={styles.row}>
        <Text style={textStyles.copyText}>부담 없이 무료 면접 도전해보세요</Text>
        <MockInterviewButton
          label="무료 면접 시작하기"
          trackMixpanelEvent={trackMixpanelEvent}
          onClick={phoneModalHandlers.open}
        />
      </Box>
      <VerifyPhoneModal
        isOpen={isPhoneModalOpen}
        onClose={phoneModalHandlers.close}
        spot="mock-interview"
        onVerifySuccess={handleVerifySuccess}
      />
    </>
  );
}

/** 신규(이용권 이력 없음) 분기: 클릭 시 동의 모달 → 무료 이용권 발급 → 시작 페이지로 이동한다. */
function StartFreeMockInterviewButton({ trackMixpanelEvent }: WithTrackMixpanelEvent) {
  const queryClient = useQueryClient();
  const { mutate, isLoading: isIssuing } = useMockInterviewCreateFreeMutation({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMockInterviewVoucherUserTypeQueryKey() });
      },
    },
  });
  const [isConsentModalOpen, consentModalHandlers] = useDisclosure(false);

  const handleConsentAgree = () => {
    consentModalHandlers.close();
    mutate(undefined, {
      onSuccess: (data) => {
        const mockInterviewId = data?.data?.mockInterviewId;

        if (mockInterviewId == null) {
          openMockInterviewStartErrorAlert();
          return;
        }

        window.location.href = AI_INTERVIEWS_URL.SESSION(mockInterviewId);
      },
      onError: openMockInterviewStartErrorAlert,
    });
  };

  return (
    <>
      <Box css={styles.row}>
        <Text style={textStyles.copyText}>부담 없이 무료 면접 도전해보세요</Text>
        <MockInterviewButton
          label="무료 면접 시작하기"
          trackMixpanelEvent={trackMixpanelEvent}
          loading={isIssuing}
          onClick={consentModalHandlers.open}
        />
      </Box>
      <MockInterviewConsentModal
        opened={isConsentModalOpen}
        onClose={consentModalHandlers.close}
        onAgree={handleConsentAgree}
      />
    </>
  );
}

/**
 * AG-316 전시용 분기: 이용권 이력과 무관하게(카운트 없이) 클릭할 때마다 이용권+면접을 새로 발급받아
 * 세션으로 바로 진입한다. 리스트 페이지를 거치지 않는다는 점에서 위 신규 분기와 다르다.
 */
function StartExhibitionMockInterviewButton({ trackMixpanelEvent }: WithTrackMixpanelEvent) {
  const { mutate, isLoading: isIssuing } = useMockInterviewNoLimitCreateMutation({});
  const [isConsentModalOpen, consentModalHandlers] = useDisclosure(false);

  const handleConsentAgree = () => {
    consentModalHandlers.close();
    mutate(undefined, {
      onSuccess: (data) => {
        const mockInterviewId = data?.data?.mockInterviewId;

        if (mockInterviewId != null) {
          window.location.href = AI_INTERVIEWS_URL.SESSION(mockInterviewId);
        }
      },
    });
  };

  return (
    <>
      <Box css={styles.row}>
        <Text style={textStyles.copyText}>부담 없이 무료 면접 도전해보세요</Text>
        <MockInterviewButton
          label="무료 면접 시작하기"
          trackMixpanelEvent={trackMixpanelEvent}
          loading={isIssuing}
          onClick={consentModalHandlers.open}
        />
      </Box>
      <MockInterviewConsentModal
        opened={isConsentModalOpen}
        onClose={consentModalHandlers.close}
        onAgree={handleConsentAgree}
      />
    </>
  );
}

/**
 * 무료소진·이용권없음(진행중 이탈 포함) 분기: 클릭 시 내 면접 리스트로 이동한다.
 * 결제 모달을 직접 열지 않고 `openPayment` 쿼리로 신호만 보낸다 — 실제로 모달을 여는 건
 * 리스트 페이지(AG-319, 미착수)의 책임이다.
 */
function StartPaidMockInterviewButton({
  trackMixpanelEvent,
  product,
  isProductLoading,
}: WithTrackMixpanelEvent & {
  product?: MockInterviewPriceCopyProps;
  isProductLoading: boolean;
}) {
  const handleClick = () => {
    window.location.href = MY_URL.AI_INTERVIEWS({ openPayment: true });
  };

  return (
    <Box css={[styles.row, styles.rowWithPrice]}>
      <Box css={styles.priceRow}>
        {product && (
          <MockInterviewPriceCopy
            regularPrice={product.regularPrice}
            paymentPrice={product.paymentPrice}
          />
        )}
      </Box>
      <MockInterviewButton
        label="모의면접 응시하기"
        trackMixpanelEvent={trackMixpanelEvent}
        loading={isProductLoading}
        disabled={isProductLoading}
        skipEnvironmentGuide
        onClick={handleClick}
      />
    </Box>
  );
}

/**
 * 무료소진·이용권 보유 분기: 클릭 시 내 면접 리스트로 이동한다(동의 모달 없이 바로 이동 — 즉시 시작 아님).
 */
function RepurchaseMockInterviewButton({ trackMixpanelEvent }: WithTrackMixpanelEvent) {
  const handleClick = () => {
    window.location.href = MY_URL.AI_INTERVIEWS();
  };

  return (
    <Box css={styles.row}>
      <Text style={textStyles.copyText}>실전 감각, 여기서 멈추기 아깝잖아요</Text>
      <MockInterviewButton
        label="다시 도전하기"
        trackMixpanelEvent={trackMixpanelEvent}
        skipEnvironmentGuide
        onClick={handleClick}
      />
    </Box>
  );
}

type MockInterviewPriceCopyProps = {
  regularPrice: number;
  paymentPrice: number;
};

/** 정가/할인가 표시 — 하드코딩하지 않고 상품 API 값을 그대로 계산해 보여준다. */
function MockInterviewPriceCopy({ regularPrice, paymentPrice }: MockInterviewPriceCopyProps) {
  const discountPercent = Math.round((1 - paymentPrice / regularPrice) * 100);

  return (
    <>
      <Box css={styles.priceBoldGroup}>
        <Text component="span" c="red.6" style={textStyles.priceBold}>
          {discountPercent}%
        </Text>
        <Text component="span" c="gray.9" style={textStyles.priceBold}>
          {paymentPrice.toLocaleString()}원
        </Text>
      </Box>
      <Text component="span" c="gray.6" style={textStyles.priceRegular}>
        {regularPrice.toLocaleString()}원
      </Text>
    </>
  );
}

const styles = {
  cta: css({
    flex: 'none',
    height: 42,
    minWidth: 200,
    padding: '0 23px',
    borderRadius: 8,
    border: 'none',
    background: '#25262B',
    color: '#fff',
    fontSize: LANDING_FONT.cta,
    fontWeight: 600,
    transition: `background ${LANDING_EASE}, transform ${LANDING_EASE}`,
    '&:hover': {
      background: '#373A40',
    },
    '&:active': {
      transform: 'scale(.98)',
    },
    '&:disabled, &[data-disabled]': {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
    [LANDING_MOBILE_MQ]: {
      flex: '1 0 0',
      width: '100%',
      minWidth: 0,
    },
  }),
  ctaLabel: css({
    color: '#fff',
    fontSize: LANDING_FONT.cta,
    fontWeight: 600,
    letterSpacing: '-0.01em',
  }),
  row: css({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flex: '1 1 0%',
    minWidth: 0,
  }),
  rowWithPrice: css({
    [LANDING_MOBILE_MQ]: {
      '& > button': {
        flex: 'none',
        width: 'auto',
      },
    },
  }),
  priceRow: css({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: '1 1 0%',
    minWidth: 0,
    [LANDING_MOBILE_MQ]: {
      flexDirection: 'column-reverse',
      alignItems: 'flex-start',
      justifyContent: 'center',
      gap: 2,
    },
  }),
  priceBoldGroup: css({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  }),
};

/** DS Text 기본 스타일이 emotion `css` prop보다 우선순위가 높아 덮어써져, 타이포 오버라이드를 `style`(inline)로 강제한다. */
const textStyles: Record<string, CSSProperties> = {
  copyText: {
    flex: '1 1 0%',
    minWidth: 0,
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#212529',
  },
  priceBold: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
  },
  priceRegular: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    textDecoration: 'line-through',
  },
};
